import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardFooter, Container, Row, Col } from 'reactstrap';
import { CardHeader, PseudoStates, colors } from '@cordata/buffet';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Calendar from 'react-calendar';
import { format, isSameDay, startOfMonth, endOfMonth } from 'date-fns';
import { all, spread } from 'axios';
import { groupBy, map, mapValues } from 'lodash';
import styled from 'styled-components';

import { authorizedPyInstance as axios } from '~/utils';

const SCalendar = styled(Calendar)`
  border: 0 !important;

  .react-calendar__tile {
    &.has-events {
      color: ${colors[2]} !important;
      font-weight: bold;
    }
  }

  .react-calendar__tile--active {
    background-color: ${colors[7]} !important;
    color: white !important;

    &.has-events {
      background-color: ${colors[2]} !important;
      color: white !important;
    }
  }
`;

const HomeCalendarCard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventsForDate, setEventsForDate] = useState([]);
  const [calendarActiveStartDate, setCalendarActiveStartDate] =
    useState(startOfMonth(selectedDate));
  const [eventsForMonth, setEventsForMonth] = useState([]);

  useEffect(() => {
    const monthStartDate = format(startOfMonth(calendarActiveStartDate));
    const monthEndDate = format(endOfMonth(calendarActiveStartDate));

    const queryString = (e) => {
      const qs = [
        `event_date__>>=${monthStartDate}`,
        `event_date__<<=${monthEndDate}`,
      ];

      if (e !== 'medical_event') {
        ['scheduled', 'requested', 'pending pre-auth', 'completed'].forEach(statusName =>
          qs.push(`status_name=${statusName}`));
      }

      return encodeURI(qs.join('&'));
    };

    all([
      axios.get(`/api/data/public/nav-list-view-medical-event?${queryString('medical_event')}`),
      axios.get(`/api/data/public/nav-list-view-appointment?${queryString('appointment')}`),
    ])
      .then(spread((medicalEvents, appointments) => ([
        medicalEvents.data.data,
        appointments.data.data,
      ])))
      .then(spread((medicalEvents, appointments) => {
        const m = medicalEvents.map(me => ({
          label: me.attributes.medicalEventLabel,
          date: me.attributes.eventDate,
        }));

        const a = appointments.map(app => ({
          label: app.attributes.appointmentTypeLabel,
          date: app.attributes.eventDateNoTz,
        }));

        setEventsForMonth([...m, ...a]);
      }))
      .catch(e => setError(e))
      .then(() => setIsLoading(false));
  }, [calendarActiveStartDate]);

  useEffect(() => {
    setEventsForDate(mapValues(groupBy(eventsForMonth.filter(e => isSameDay(e.date, selectedDate)), 'label'), v => v.length));
  }, [selectedDate, eventsForMonth]);

  return (
    <Card>
      <CardHeader className="text-light" color="primary">
        Calendar
      </CardHeader>

      <CardBody>
        <Container>
          <Row>
            <Col>
              <SCalendar
                value={selectedDate}
                onChange={setSelectedDate}
                onActiveStartDateChange={({ activeStartDate }) =>
                    setCalendarActiveStartDate(activeStartDate)}
                calendarType="US"
                tileClassName={({ date, view }) => (view === 'month' && eventsForMonth.some(e => isSameDay(e.date, date)) ? 'has-events' : 'text-dark')}
              />
            </Col>

            <Col>
              <div className="text-center mb-3"><strong>Events on {format(selectedDate, 'MMM Do')}</strong></div>

              {!isLoading && !error ? (
                <>
                  {map(eventsForDate, (count, label) => (
                    <div key={label} className="mb-2">
                      <span className="text-secondary"><strong>{count}</strong></span> {label}
                    </div>
                  ))}

                  {!Object.keys(eventsForDate).length && (
                    <div>
                      No events found!
                    </div>
                  )}
                </>
              ) : (
                <PseudoStates isLoading={isLoading} error={error} />
              )}
            </Col>
          </Row>
        </Container>
      </CardBody>

      <CardFooter className="text-center" tag={Link} to={`/home/calendar?startDate=${format(calendarActiveStartDate)}`}>
        See Full Calendar <FontAwesomeIcon icon="chevron-right" size="sm" />
      </CardFooter>
    </Card>
  );
};

export default HomeCalendarCard;
